(function (globalScope) {
  "use strict";

  const globalObject =
    typeof globalScope !== "undefined"
      ? globalScope
      : typeof global !== "undefined"
      ? global
      : this;
  const DEFAULT_TIMEOUT = 7000;
  const RETRY_ATTEMPTS = 1;
  const CART_UPDATE_ENDPOINT = "/cart/update.js";
  const FNV_OFFSET_BASIS = 2166136261;

  function ensureFetch() {
    if (typeof globalObject.fetch !== "function") {
      throw new Error("Fetch API is not available.");
    }
    return globalObject.fetch.bind(globalObject);
  }

  function getCartUrl() {
    const base =
      (globalObject && globalObject.routes && globalObject.routes.cart_url) ||
      (globalObject &&
        globalObject.Shopify &&
        globalObject.Shopify.routes &&
        globalObject.Shopify.routes.cart_url) ||
      "/cart";
    return `${base}.js`;
  }

  function sanitizeSingleLine(value) {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value).replace(/\s+/g, " ").trim();
  }

  function clampString(value) {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value);
  }

  function buildRequestOptions(options = {}) {
    return {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(options.headers || {}),
      },
      ...options,
    };
  }

  function withTimeout(promiseFactory, timeout = DEFAULT_TIMEOUT) {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const fetchPromise = promiseFactory(
      controller ? controller.signal : undefined
    );

    if (!timeout || !controller) {
      return fetchPromise;
    }

    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        const error = new Error("Request timed out");
        error.name = "TimeoutError";
        reject(error);
      }, timeout);
    });

    return Promise.race([
      fetchPromise.finally(() => clearTimeout(timeoutId)),
      timeoutPromise,
    ]);
  }

  async function fetchJson(url, options = {}, timeout) {
    const fetchImpl = ensureFetch();
    const requestOptions = buildRequestOptions(options);

    const response = await withTimeout(
      (signal) => fetchImpl(url, { ...requestOptions, signal }),
      timeout
    );

    if (!response.ok) {
      const error = new Error(`Request failed with status ${response.status}`);
      error.status = response.status;
      error.url = url;
      throw error;
    }

    const contentType =
      response.headers && response.headers.get
        ? response.headers.get("content-type")
        : null;
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  }

  function hashString(input) {
    const value = sanitizeSingleLine(input);
    if (!value) {
      return "";
    }

    let hash = FNV_OFFSET_BASIS;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash +=
        (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
      hash >>>= 0; // ensure unsigned 32-bit
    }

    return hash.toString(16).padStart(8, "0").slice(0, 12);
  }

  function buildAttributes({
    isoTimestamp,
    version,
    path,
    url,
    userAgent,
    hash,
    cartToken,
  }) {
    const attributes = {
      accepted_tos: "true",
      accepted_tos_ts: clampString(isoTimestamp),
      accepted_tos_version: clampString(version),
      accepted_tos_path: clampString(path),
      accepted_tos_url: clampString(url),
      ua: clampString(userAgent),
    };

    if (hash) {
      attributes.accepted_tos_hash = clampString(hash);
    }

    if (cartToken) {
      attributes.cart_token = clampString(cartToken);
    }

    return { attributes };
  }

  function buildNoteLine({
    version,
    isoTimestamp,
    path,
    url,
    userAgent,
    hash,
    cartToken,
  }) {
    const parts = [
      `TOS ${clampString(version)} accepted @ ${clampString(
        isoTimestamp
      )} (${clampString(path)})`,
      `url=${clampString(url)}`,
      `ua=${sanitizeSingleLine(userAgent)}`,
    ];

    if (hash) {
      parts.push(`hash=${clampString(hash)}`);
    }

    if (cartToken) {
      parts.push(`token=${clampString(cartToken)}`);
    }

    return parts.join(" ");
  }

  async function getCart(timeout) {
    return fetchJson(getCartUrl(), undefined, timeout);
  }

  async function updateCart(payload, timeout) {
    return fetchJson(
      CART_UPDATE_ENDPOINT,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
      timeout
    );
  }

  function shouldSkipUpdate(cart, isoTimestamp) {
    if (!cart || !cart.attributes) {
      return false;
    }

    const existingAccepted = cart.attributes.accepted_tos;
    const existingTimestamp = cart.attributes.accepted_tos_ts;

    if (String(existingAccepted).toLowerCase() !== "true") {
      return false;
    }

    if (!existingTimestamp) {
      return false;
    }

    return String(existingTimestamp) >= String(isoTimestamp);
  }

  function needsNoteUpdate(existingNote, newLine) {
    if (!newLine) {
      return false;
    }

    if (!existingNote) {
      return true;
    }

    const lines = existingNote
      .split("\n")
      .map((line) => sanitizeSingleLine(line))
      .filter(Boolean);

    return !lines.includes(sanitizeSingleLine(newLine));
  }

  async function attemptRecord(options) {
    const {
      version,
      path,
      url,
      userAgent,
      hashSource,
      includeNote = true,
      timeout = DEFAULT_TIMEOUT,
      cartToken,
    } = options;

    const isoTimestamp = new Date().toISOString();

    const cart = await getCart(timeout);

    if (shouldSkipUpdate(cart, isoTimestamp)) {
      return {
        alreadyAccepted: true,
        timestamp: cart.attributes.accepted_tos_ts,
        noteUpdated: false,
        noteLine: null,
        hash: "",
        cartToken: cart.token || null,
      };
    }

    const userAgentValue =
      userAgent ||
      (typeof navigator !== "undefined" ? navigator.userAgent : "");
    const urlValue =
      url ||
      (typeof window !== "undefined" && window.location
        ? window.location.href
        : "");
    const token = cartToken || (cart && cart.token ? cart.token : null);
    const hash = hashSource ? hashString(hashSource) : "";

    const attributePayload = buildAttributes({
      isoTimestamp,
      version,
      path,
      url: urlValue,
      userAgent: userAgentValue,
      hash,
      cartToken: token,
    });

    let payload = { ...attributePayload };
    let noteLine = null;
    let noteUpdated = false;
    if (includeNote) {
      noteLine = buildNoteLine({
        version,
        isoTimestamp,
        path,
        url: urlValue,
        userAgent: userAgentValue,
        hash,
        cartToken: token,
      });

      if (needsNoteUpdate(cart.note, noteLine)) {
        const nextNote =
          cart.note && cart.note.length
            ? `${cart.note}\n${noteLine}`
            : noteLine;
        payload = { ...payload, note: nextNote };
        noteUpdated = true;
      }
    }

    await updateCart(payload, timeout);

    return {
      alreadyAccepted: false,
      timestamp: isoTimestamp,
      noteUpdated,
      noteLine,
      hash,
      cartToken: token,
    };
  }

  async function record(options = {}) {
    const resolvedOptions = {
      version: "v1.0",
      path: "cart",
      includeNote: true,
      ...options,
    };

    let lastError;

    for (let attempt = 0; attempt <= RETRY_ATTEMPTS; attempt += 1) {
      try {
        return await attemptRecord(resolvedOptions);
      } catch (error) {
        lastError = error;
        if (attempt >= RETRY_ATTEMPTS) {
          throw error;
        }
      }
    }

    throw lastError;
  }

  const TermsConsentAudit = {
    record,
    buildAttributes,
    buildNoteLine,
    hash: hashString,
    constants: {
      DEFAULT_TIMEOUT,
      RETRY_ATTEMPTS,
    },
  };

  if (typeof module !== "undefined" && module.exports) {
    module.exports = TermsConsentAudit;
  }

  globalObject.TermsConsentAudit = TermsConsentAudit;
})(
  typeof window !== "undefined"
    ? window
    : typeof global !== "undefined"
    ? global
    : undefined
);
